import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import RelationshipQuery from "@arcgis/core/rest/support/RelationshipQuery.js";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import { AppConfig } from "../AppConfig";
import { JobsStatus, JobsType } from "../constants/Jobs";
import {
    applyEdits,
    getAllRelatedFeatures,
    getLayerName,
    getMapView,
    getNfsLayer,
    isMainFeatureByLayerId,
} from "./arcgisUtils";
import { createWorkOrder, updateFDVObjectNew } from "./FDVUtils";
import { dispatchRefreshFeature, uuidv4, zoomToObject } from "./helpers";
import { getValidationResult, NFSStatus } from "./nfsUtils";
import {
    FeatureObject,
    reconcileVersion,
    FeatureWithChanges,
    getDifferencesObjectIds,
    stopEditing,
    stopReading,
    startReading,
    startEditing,
    postChanges,
    reconcileOrAbortOnConflicts,
} from "./newVersioningUtils";
import { toast } from "react-toastify";
import { exportFeatureToSksd } from "./sksdUtils";
import { generateEFSMessageForRemovedOrDiscontinuedObject } from "./efsUtils";
import { FeatureClasses, NauticalClasses } from "../constants/NauticalObjects";
import * as distanceOperator from "@arcgis/core/geometry/operators/distanceOperator.js";
import { AttributeValidationError, NauticalObjectValidationResult } from "../types/types";
import { JaNei } from "../constants/nfsDomains";
import { buildJobsWhere, escapeSqlLiteral, type JobsQueryParams } from "./jobsQuery";
import { filterJobsForActiveVersion, uniqueIds } from "./existingJobsForObject";
import type arcgisCoreMap from "@arcgis/core/Map";
import type { FeatureIdentifier, FeatureEditResult } from "@arcgis/core/editing/types";

const JobsUrl = (): string => AppConfig.AdminFeatureServer + AppConfig.JobsConfig.JobsLayerId;
const WorkOrderUrl = (): string => AppConfig.AdminFeatureServer + AppConfig.JobsConfig.WorkOrderLayerId;
const StepsUrl = (): string => AppConfig.AdminFeatureServer + AppConfig.JobsConfig.StepsLayerId;

const JOBS_PAGE_SIZE = 1000;

/** Page size for the jobs panel list. Distinct from JOBS_PAGE_SIZE, which getJobs still uses. */
export const JOBS_PAGE_SIZE_UI = 50;

/** Max Steps rows fetched when resolving the Plan-job globalid set for a version. */
const PLAN_STEPS_PREFILTER_LIMIT = 2000;

export const createNewJob = async (
    jobName: string,
    jobDescription: string,
    jobType: JobsType,
    sjomerkeNumre?: string
): Promise<{ globalId: string | null | undefined; objectId: number | null | undefined } | null | undefined> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const newJob = new Graphic({
        attributes: {
            navn: jobName,
            beskrivelse: jobDescription,
            jobb_type: jobType,
            sjomerke_nr: sjomerkeNumre,
        },
    });

    const result = await applyEdits(
        jobsTableLayer,
        {
            addFeatures: [newJob],
        },
        { rollbackOnFailureEnabled: true }
    );
    if (result.addFeatureResults.length > 0) {
        return { globalId: result.addFeatureResults[0].globalId, objectId: result.addFeatureResults[0].objectId };
    } else {
        return null;
    }
};

export const getJobs = async (status: JobsStatus, offset = 0): Promise<{ features: Graphic[]; hasMore: boolean }> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const query = jobsTableLayer.createQuery();
    query.where = status === JobsStatus.Alle ? "1=1" : `status = ${status}`;
    query.where += " AND opprettet_dato >= date '2026-01-01'";
    // query.where = "status NOT IN (4, 5, 6, 9, 10)";
    // query.where = "globalid = '{B7835FB2-3F21-4272-8C35-B4C2CC208409}'"
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.start = offset;
    query.num = JOBS_PAGE_SIZE;
    query.orderByFields = ["oppdatert_dato DESC"];

    const result = await jobsTableLayer.queryFeatures(query);
    return { features: result.features, hasMore: result.exceededTransferLimit ?? false };
};

export const getJobByObjectId = async (objectId: number): Promise<Graphic | null> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const query = jobsTableLayer.createQuery();
    query.where = `objectid = ${objectId}`;
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.num = 1;

    const result = await jobsTableLayer.queryFeatures(query);
    return result.features[0] ?? null;
};

export const getJobByGlobalId = async (globalId: string): Promise<Graphic | null> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const query = jobsTableLayer.createQuery();
    query.where = `globalid = '${globalId}'`;
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.num = 1;

    const result = await jobsTableLayer.queryFeatures(query);
    return result.features[0] ?? null;
};

/**
 * Globalids of the Plan jobs that belong to a given version.
 *
 * createJobAndSetAsPlan writes exactly one step per Plan job, with jobb_status = 1 and
 * kommentar = "Version:{GUID}". Querying that comment inverts the relationship so the
 * version gate can be expressed as a where clause on the jobs layer instead of being
 * applied in render.
 *
 * Returns [] when there is no active version, which produces the same "no plan jobs"
 * outcome as the sentinel the render-time filter used.
 */
export const getPlanJobGlobalIdsForVersion = async (versionGuid: string | null | undefined): Promise<string[]> => {
    if (!versionGuid) {
        return [];
    }

    const stepsLayer = new FeatureLayer({
        url: StepsUrl(),
        id: "jobsStepsLayer",
        title: "JobsSteps",
    });

    const query = stepsLayer.createQuery();
    query.where = `jobb_status = ${JobsStatus.Plan} AND kommentar LIKE '%${escapeSqlLiteral(versionGuid)}%'`;
    query.outFields = ["jobb_globalid"];
    query.returnGeometry = false;
    query.num = PLAN_STEPS_PREFILTER_LIMIT;

    const result = await stepsLayer.queryFeatures(query);
    if (result.exceededTransferLimit) {
        console.warn(
            `Flere enn ${PLAN_STEPS_PREFILTER_LIMIT} Plan-steg funnet for versjon ${versionGuid}. ` +
                "Listen med Plan-jobber for denne versjonen kan være ufullstendig."
        );
    }
    const globalIds = result.features
        .map((feature) => feature.attributes.jobb_globalid as string | null)
        .filter((id): id is string => !!id);

    return [...new Set(globalIds)];
};

/**
 * One page of jobs matching params. `page` is 1-based.
 *
 * The objectid tiebreaker is mandatory: offset paging over oppdatert_dato DESC alone is
 * unstable when timestamps tie, which duplicates rows across pages or skips them.
 */
export const queryJobsPage = async (params: JobsQueryParams, page: number, pageSize: number): Promise<Graphic[]> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const query = jobsTableLayer.createQuery();
    query.where = buildJobsWhere(params);
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.start = (page - 1) * pageSize;
    query.num = pageSize;
    query.orderByFields = ["oppdatert_dato DESC", "objectid DESC"];

    const result = await jobsTableLayer.queryFeatures(query);
    return result.features;
};

/** Total number of jobs matching params, for the hit count and page count. */
export const queryJobsCount = async (params: JobsQueryParams): Promise<number> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const query = jobsTableLayer.createQuery();
    query.where = buildJobsWhere(params);
    query.returnGeometry = false;

    return await jobsTableLayer.queryFeatureCount(query);
};

/**
 * Creates FDV objects for the features in the job if they do not already exist in FDV
 * Use shouldCreateWorkOrder to also create work orders for the created FDV objects
 * @param jobObjectId
 * @param jobName
 * @param featuresWithChanges
 * @param versionName
 * @param shouldCreateWorkOrder
 * @returns
 */
export const createWorkOrderForJob = async (
    jobObjectId: string,
    jobName: string,
    featuresWithChanges: FeatureWithChanges[],
    versionName: string,
    shouldCreateWorkOrder: boolean = false
): Promise<string[]> => {
    if (!versionName) return [];

    const fdvResults: any[] = [];

    const addedFDVKeys = new Set<string>();
    for (const feature of featuresWithChanges) {
        // If main feature is overett layer 7, 8 or sjøkabel 13 we need to create fdv objects for related light / fast sjomerke
        if (
            feature.mainFeature.layerId === 7 ||
            feature.mainFeature.layerId === 8 ||
            feature.mainFeature.layerId === 13
        ) {
            for (const relatedFeature of feature.relatedFeatures) {
                if (
                    (feature.mainFeature.layerId === 7 && relatedFeature.layerId === 0) ||
                    (feature.mainFeature.layerId === 8 && relatedFeature.layerId === 5) ||
                    (feature.mainFeature.layerId === 13 && relatedFeature.layerId === 0)
                ) {
                    // Create FDV objects for related light
                    const relVer = relatedFeature.versionGraphic;
                    const relGlobalIdRaw = relVer?.attributes.globalid;
                    if (!relVer || !relGlobalIdRaw) continue;

                    const relGlobalId = String(relGlobalIdRaw).replace("{", "").replace("}", "");
                    const relKey = `${relatedFeature.layerId}-${relGlobalId}`;

                    // Skip if object already exists in FDV
                    if (relVer.attributes.gis_obj_id !== undefined && relVer.attributes.gis_obj_id !== null) {
                        continue;
                    }
                    // Skip if we've already scheduled this (layerId + globalId)
                    if (addedFDVKeys.has(relKey)) {
                        continue;
                    }

                    try {
                        const res = await updateFDVObjectNew(
                            {
                                layerId: relatedFeature.layerId,
                                globalId: relGlobalId,
                                gdbVersion: versionName,
                            },
                            relVer,
                            true
                        );
                        fdvResults.push(res);
                    } catch (err) {
                        console.error("updateFDVObjectNew failed for related feature", relKey, err);
                        fdvResults.push(undefined);
                    }
                    addedFDVKeys.add(relKey);
                }
            }
        }

        // Main feature processing
        const mainVer = feature.mainFeature.versionGraphic;
        if (!mainVer) continue;

        // If main feature already has gis_obj_id, skip
        if (mainVer.attributes.gis_obj_id !== undefined && mainVer.attributes.gis_obj_id !== null) {
            continue;
        }
        // Ensure gis_obj_id property exists on attributes before attempting creation
        if (!Object.hasOwn(mainVer.attributes, "gis_obj_id")) {
            continue;
        }
        // Skip overett and sjøkabel since they do not exist in FDV
        if (
            feature.mainFeature.layerId === 7 ||
            feature.mainFeature.layerId === 8 ||
            feature.mainFeature.layerId === 13
        ) {
            continue;
        }

        const mainGlobalIdRaw = mainVer.attributes.globalid;
        if (!mainGlobalIdRaw) continue;
        const mainGlobalId = String(mainGlobalIdRaw).replace("{", "").replace("}", "");
        const mainKey = `${feature.mainFeature.layerId}-${mainGlobalId}`;

        if (addedFDVKeys.has(mainKey)) {
            continue;
        }

        try {
            const res = await updateFDVObjectNew(
                {
                    layerId: feature.mainFeature.layerId,
                    globalId: mainGlobalId,
                    gdbVersion: versionName,
                },
                mainVer,
                true
            );
            fdvResults.push(res);
        } catch (err) {
            console.error("updateFDVObjectNew failed for main feature", mainKey, err);
            fdvResults.push(undefined);
        }
        addedFDVKeys.add(mainKey);
    }
    const addedWorkorderIds: string[] = [];
    if (shouldCreateWorkOrder) {
        const addedObjects = await getJobRelatedInvolvedObjects(jobObjectId);
        if (addedObjects.length > 0) {
            const objectsToCreateWorkOrderFor: { involvedObject: Graphic; gisObjId: number; feature: FeatureObject }[] =
                [];
            for (const mainFeature of featuresWithChanges) {
                // Logic for layers that are not overett or sjøkabel
                if (
                    mainFeature.mainFeature.layerId !== 7 &&
                    mainFeature.mainFeature.layerId !== 8 &&
                    mainFeature.mainFeature.layerId !== 13
                ) {
                    // find the main feature in the updateFdvPromises
                    // filter out undefined and unsuccessful
                    const filteredFdvResults = fdvResults.filter(
                        (fdvResult) => fdvResult !== undefined && fdvResult.success
                    );
                    const featureWithGisObjid = filteredFdvResults.find(
                        (fdvResult) =>
                            fdvResult.feature?.attributes.globalid ===
                            mainFeature.mainFeature.versionGraphic?.attributes.globalid
                    );
                    let featureGisObjectId = featureWithGisObjid?.feature?.attributes.gis_obj_id;
                    featureGisObjectId ??= mainFeature.mainFeature.versionGraphic?.attributes.gis_obj_id;
                    const involvedObject = addedObjects.find(
                        (obj) =>
                            obj.attributes.nautisk_objekt_globalid ===
                            mainFeature.mainFeature.versionGraphic?.attributes.globalid
                    );
                    if (!involvedObject || !featureGisObjectId) continue;
                    // If Main feature has attribute
                    objectsToCreateWorkOrderFor.push({
                        involvedObject: involvedObject,
                        gisObjId: featureGisObjectId,
                        feature: mainFeature.mainFeature,
                    });
                } else {
                    // Logic for overett features
                    // Overett does not exist in FDV so we need to create work order for related light / fast sjomerke
                    // Sjøkabel and elektroskap does not exist but sjøkabel can be connected to light
                    for (const relatedFeature of mainFeature.relatedFeatures) {
                        if (
                            (mainFeature.mainFeature.layerId === 7 && relatedFeature.layerId === 0) ||
                            (mainFeature.mainFeature.layerId === 8 && relatedFeature.layerId === 5) ||
                            (mainFeature.mainFeature.layerId === 13 && relatedFeature.layerId === 0)
                        ) {
                            // Create work order for related light sjomerke
                            const filteredFdvResults = fdvResults.filter(
                                (fdvResult) => fdvResult !== undefined && fdvResult.success
                            );
                            const featureWithGisObjid = filteredFdvResults.find(
                                (fdvResult) =>
                                    fdvResult.feature?.attributes.globalid ===
                                    relatedFeature.versionGraphic?.attributes.globalid
                            );
                            let featureGisObjectId = featureWithGisObjid?.feature?.attributes.gis_obj_id;
                            featureGisObjectId ??= relatedFeature.versionGraphic?.attributes.gis_obj_id;
                            // Use mainfeature involved object
                            const involvedObject = addedObjects.find(
                                (obj) =>
                                    obj.attributes.nautisk_objekt_globalid ===
                                    mainFeature.mainFeature.versionGraphic?.attributes.globalid
                            );
                            if (!involvedObject || !featureGisObjectId) continue;
                            // If Main feature has attribute
                            objectsToCreateWorkOrderFor.push({
                                involvedObject: involvedObject,
                                gisObjId: featureGisObjectId,
                                feature: relatedFeature,
                            });
                        }
                    }
                }
            }

            const existingWorkOrders = await getJobWorkOrdersFromInvolvedObjects(
                objectsToCreateWorkOrderFor.map((obj) => obj.involvedObject.attributes.globalid)
            );
            for (const object of objectsToCreateWorkOrderFor) {
                const alreadyHasWorkOrder = existingWorkOrders.find(
                    (workOrder) =>
                        workOrder.attributes.inv_nautisk_objekt_globalid === object.involvedObject.attributes.globalid
                );
                if (alreadyHasWorkOrder) continue;
                const typeCode = getWorkOrderTypeCodeForObject(object.feature);
                if (!typeCode) continue;
                const workOrderId = await createWorkOrder(
                    { description: `${jobName}`, gisObjId: object.gisObjId },
                    typeCode
                );
                if (!workOrderId) continue;
                addedWorkorderIds.push(workOrderId);
                await addWorkOrderToInvolvedObject(object.involvedObject.attributes.globalid, workOrderId);
            }
        }
    }
    return addedWorkorderIds;
};

const getWorkOrderTypeCodeForObject = (feature: FeatureObject): string | null => {
    const featureGraphic = feature.versionGraphic ?? feature.defaultGraphic;
    if (!featureGraphic) {
        return null;
    }
    const nfsStatus = featureGraphic.attributes.nfs_status;
    switch (nfsStatus) {
        case NFSStatus.PROJECTED:
            return "PR";
        case NFSStatus.PROJECTED_REMOVED:
            return "FJ";
        case NFSStatus.PROJECTED_DISCONTINUED:
            return "FO";
        case NFSStatus.PROJECTED_EXISTING:
            // For now, no work order for modification
            return null;
        default:
            return null;
    }
};

let _relatedObjectsLayer: FeatureLayer | undefined;
const getRelatedObjectsLayer = () => {
    _relatedObjectsLayer ??= new FeatureLayer({
        url: AppConfig.AdminFeatureServer + AppConfig.JobsConfig.RelatedObjectsLayerId,
        id: "RelatedObjectsLayer",
        title: "RelatedObjects",
    });
    return _relatedObjectsLayer;
};

/**
 * Plan and QA1 jobs that already involve any of the given nautical objects, newest first.
 * Plan jobs only count when they belong to the given version.
 *
 * Matches on the related-objects table (exact globalids) rather than the job's sjomerke_nr
 * text, which would need LIKE matching and give partial number hits.
 */
export const getOpenJobsForObject = async (
    objectGlobalIds: string[],
    versionGuid: string | null | undefined
): Promise<Graphic[]> => {
    const ids = uniqueIds(objectGlobalIds);
    if (ids.length === 0) {
        return [];
    }

    const relatedObjectsLayer = getRelatedObjectsLayer();
    const relatedQuery = relatedObjectsLayer.createQuery();
    relatedQuery.where = `nautisk_objekt_globalid IN (${ids.map((id) => `'${escapeSqlLiteral(id)}'`).join(",")})`;
    relatedQuery.outFields = ["jobb_globalid"];
    relatedQuery.returnGeometry = false;

    const related = await relatedObjectsLayer.queryFeatures(relatedQuery);
    const jobGlobalIds = uniqueIds(related.features.map((feature) => feature.attributes.jobb_globalid));
    if (jobGlobalIds.length === 0) {
        return [];
    }

    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    const jobsQuery = jobsTableLayer.createQuery();
    jobsQuery.where =
        `globalid IN (${jobGlobalIds.map((id) => `'${escapeSqlLiteral(id)}'`).join(",")})` +
        ` AND status IN (${JobsStatus.Plan}, ${JobsStatus.QA1})`;
    jobsQuery.outFields = ["*"];
    jobsQuery.returnGeometry = false;
    jobsQuery.orderByFields = ["oppdatert_dato DESC", "objectid DESC"];

    const jobs = (await jobsTableLayer.queryFeatures(jobsQuery)).features;
    if (jobs.length === 0) {
        return [];
    }

    const hasPlanJobs = jobs.some((job) => job.attributes.status === JobsStatus.Plan);
    const planJobIdsForVersion = hasPlanJobs ? await getPlanJobGlobalIdsForVersion(versionGuid) : [];
    return filterJobsForActiveVersion(jobs, planJobIdsForVersion);
};

export const addObjectToJobSimple = async (
    jobGlobalId: string,
    nautiskObjektGlobalId: string,
    nautiskObjektKilde: number
): Promise<string | null | undefined> => {
    const result = await applyEdits(
        getRelatedObjectsLayer(),
        {
            addFeatures: [
                new Graphic({
                    attributes: {
                        jobb_globalid: jobGlobalId,
                        nautisk_objekt_globalid: nautiskObjektGlobalId,
                        nautisk_objekt_kilde: nautiskObjektKilde,
                    },
                }),
            ],
        },
        { rollbackOnFailureEnabled: true }
    );

    if (result.addFeatureResults.length === 1) {
        return result.addFeatureResults[0].globalId;
    } else {
        return null;
    }
};

/**
 * Creates a recovery job for a single feature when a direct-to-default SKSD send fails.
 * The job is created in the FailedSendingToSksd status so the user can retry via the
 * existing JobFailedSksdSending screen.
 *
 * Returns the job's globalid on success, null on failure.
 */
export const createFailedSksdJobForFeature = async (
    feature: Graphic,
    sksdErrorMessage: string
): Promise<string | null> => {
    const layer = feature.layer as FeatureLayer;
    const layerId = layer?.layerId;
    if (layerId === undefined || layerId === null) {
        toast.error("Kunne ikke opprette retry-jobb: mangler lag-informasjon.");
        return null;
    }

    const sjomerkeNr = feature.attributes.sjomerke_nr as string | undefined;
    const navn = feature.attributes.navn as string | undefined;
    const jobName = `SKSD-retry: ${sjomerkeNr ?? navn ?? feature.attributes.globalid}`;
    const jobDescription = `Automatisk opprettet etter mislykket SKSD-sending fra direktepost. Feil: ${sksdErrorMessage}`;

    const job = await createNewJob(jobName, jobDescription, JobsType.MODIFICATION, sjomerkeNr);
    if (!job?.globalId) {
        toast.error("Kunne ikke opprette retry-jobb.");
        return null;
    }

    const relationshipId = await addObjectToJobSimple(job.globalId, feature.attributes.globalid, layerId);
    if (!relationshipId) {
        toast.error("Retry-jobb opprettet, men kunne ikke knytte objektet til jobben.");
        return job.globalId; // job exists; user can still attach manually
    }

    const stepId = await createNewJobStep(job.globalId, JobsStatus.FailedSendingToSksd, sksdErrorMessage);
    if (!stepId) {
        toast.error("Retry-jobb opprettet, men kunne ikke sette status til 'Failed sending to SKSD'.");
        return job.globalId;
    }

    return job.globalId;
};

/**
 * Use when creating a new job and immediately adding objects to it
 * @param jobGlobalId
 * @param jobObjectId
 * @param jobName
 * @param featuresWithChanges
 * @param versionName
 * @param shouldCreateWorkOrder
 * @param shouldCreateFDVObjects
 * @returns
 */
export const addObjectsToJobNew = async (
    jobGlobalId: string,
    jobObjectId: string,
    jobName: string,
    featuresWithChanges: FeatureWithChanges[],
    versionName: string,
    shouldCreateWorkOrder: boolean,
    shouldCreateFDVObjects: boolean
): Promise<FeatureEditResult[] | null> => {
    if (!versionName) return null;

    const objectsToAdd: {
        attributes: { jobb_globalid: string; nautisk_objekt_globalid: string; nautisk_objekt_kilde: number };
    }[] = [];

    for (const featureWithChange of featuresWithChanges) {
        if (!featureWithChange.mainFeature.versionGraphic?.attributes.globalid) {
            toast.error("Feil ved tilknytning av objekt til jobb");
            return null;
        }
        // Finding objects that should be added to the job involverte nautiske objekter
        objectsToAdd.push({
            attributes: {
                jobb_globalid: jobGlobalId,
                nautisk_objekt_globalid: featureWithChange.mainFeature.versionGraphic?.attributes.globalid,
                nautisk_objekt_kilde: featureWithChange.mainFeature.layerId,
            },
        });

        // if main object is overett layer 7, 8 or sjøkabel layer 13 we need to also add related lys / fast sjømerke or elektroskap
        if ([7, 8, 13].includes(featureWithChange.mainFeature.layerId)) {
            for (const relatedFeature of featureWithChange.relatedFeatures) {
                if (
                    (featureWithChange.mainFeature.layerId === 7 && relatedFeature.layerId === 0) ||
                    (featureWithChange.mainFeature.layerId === 8 && relatedFeature.layerId === 5) ||
                    (featureWithChange.mainFeature.layerId === 13 && relatedFeature.layerId === 0) ||
                    (featureWithChange.mainFeature.layerId === 13 && relatedFeature.layerId === 12)
                ) {
                    objectsToAdd.push({
                        attributes: {
                            jobb_globalid: jobGlobalId,
                            nautisk_objekt_globalid: relatedFeature.versionGraphic?.attributes.globalid,
                            nautisk_objekt_kilde: relatedFeature.layerId,
                        },
                    });
                }
            }
        }
    }

    const result = await applyEdits(
        getRelatedObjectsLayer(),
        {
            addFeatures: objectsToAdd.map((obj) => new Graphic({ attributes: obj.attributes })),
        },
        { rollbackOnFailureEnabled: true }
    );

    if (shouldCreateFDVObjects) {
        await createWorkOrderForJob(jobObjectId, jobName, featuresWithChanges, versionName, shouldCreateWorkOrder);
    }

    if (result.addFeatureResults.length === objectsToAdd.length) {
        return result.addFeatureResults;
    } else {
        return null;
    }
};

export const addWorkOrderToInvolvedObject = async (involvedObjectGlobalId: string, workOrderId: string) => {
    const jobsWorkOrderLayer = new FeatureLayer({
        url: WorkOrderUrl(),
        id: "jobsWorkOrderLayer",
        title: "JobsWorkOrder",
    });

    const newJobStep = new Graphic({
        attributes: {
            arbeidsordre_id: workOrderId,
            inv_nautisk_objekt_globalid: involvedObjectGlobalId,
        },
    });

    const result = await applyEdits(
        jobsWorkOrderLayer,
        {
            addFeatures: [newJobStep],
        },
        { rollbackOnFailureEnabled: true }
    );

    if (result.addFeatureResults.length > 0) {
        return result.addFeatureResults[0].globalId;
    } else {
        return null;
    }
};

export const createNewJobStep = async (
    jobGlobalId: string,
    jobStep: number,
    comment?: string
): Promise<string | null | undefined> => {
    const jobsStepsLayer = new FeatureLayer({
        url: StepsUrl(),
        id: "jobsStepsLayer",
        title: "JobsSteps",
    });

    const newJobStep = new Graphic({
        attributes: {
            jobb_globalid: jobGlobalId,
            jobb_status: jobStep,
            kommentar: comment,
        },
    });

    const result = await applyEdits(
        jobsStepsLayer,
        {
            addFeatures: [newJobStep],
        },
        { rollbackOnFailureEnabled: true }
    );

    if (result.addFeatureResults.length > 0) {
        return result.addFeatureResults[0].globalId;
    } else {
        return null;
    }
};

export const createJobAndSetAsPlan = async (
    jobName: string,
    jobDescription: string,
    versioncomment: string,
    featuresWithChanges: FeatureWithChanges[],
    versionName: string,
    createWorkOrder: boolean,
    shouldCreateFDVObjects: boolean
): Promise<string | null> => {
    const mainfeatures = featuresWithChanges.map((featureWithChanges) => {
        return featureWithChanges.mainFeature;
    });

    let jobType = JobsType.UNDEFINED;
    if (mainfeatures.length === 1) {
        switch (mainfeatures[0].versionGraphic?.attributes.nfs_status) {
            case NFSStatus.OPERATIVE:
                jobType = JobsType.UNDEFINED;
                break;
            case NFSStatus.PROJECTED:
                jobType = JobsType.CREATION;
                break;
            case NFSStatus.PROJECTED_EXISTING:
                jobType = JobsType.MODIFICATION;
                break;
            case NFSStatus.PROJECTED_DISCONTINUED:
                jobType = JobsType.DISCONTINUATION;
                break;
            case NFSStatus.PROJECTED_REMOVED:
                jobType = JobsType.REMOVAL;
                break;
            default:
                jobType = JobsType.UNDEFINED;
                break;
        }
    } else if (mainfeatures.length === 2) {
        const firstFeatureStatus = mainfeatures[0].versionGraphic?.attributes.nfs_status;
        const secondFeatureStatus = mainfeatures[1].versionGraphic?.attributes.nfs_status;

        if (
            firstFeatureStatus === NFSStatus.PROJECTED &&
            (secondFeatureStatus === NFSStatus.PROJECTED_DISCONTINUED ||
                secondFeatureStatus === NFSStatus.PROJECTED_REMOVED)
        ) {
            jobType = JobsType.REPLACEMENT;
        } else if (
            (firstFeatureStatus === NFSStatus.PROJECTED_DISCONTINUED ||
                firstFeatureStatus === NFSStatus.PROJECTED_REMOVED) &&
            secondFeatureStatus === NFSStatus.PROJECTED
        ) {
            jobType = JobsType.REPLACEMENT;
        } else {
            jobType = JobsType.UNDEFINED;
        }
    }

    let sjomerkeNumre: string = "";
    for (const feature of mainfeatures) {
        if (feature.versionGraphic?.attributes.sjomerke_nr) {
            if (sjomerkeNumre.length > 0) {
                sjomerkeNumre += ", ";
            }
            sjomerkeNumre += `${feature.versionGraphic.attributes.sjomerke_nr}`;
        }
    }

    const job = await createNewJob(jobName, jobDescription, jobType, sjomerkeNumre);

    if (!job?.globalId || !job?.objectId) {
        toast.error("Kunne ikke opprette jobb");
        return null;
    }

    await addObjectsToJobNew(
        job.globalId,
        job.objectId.toString(),
        jobName,
        featuresWithChanges,
        versionName,
        createWorkOrder,
        shouldCreateFDVObjects
    );

    // version comment uses the guid of the version where this planned object lives. This way we can only show plans for the correct version
    const planStepGlobalId = await createNewJobStep(job.globalId, 1, versioncomment);
    if (planStepGlobalId) {
        // return planStepGlobalId;
        return job.globalId;
    } else {
        return null;
    }
};

export const updateJobAndSendToQA1 = async (
    featuresWithChanges: FeatureWithChanges[],
    jobStepGlobalId: string,
    comment: string,
    jobGlobalId: string,
    jobObjectId: number,
    jobName: string,
    versionGuid: string,
    versionName: string,
    username: string
): Promise<boolean> => {
    let success = false;

    const sessionID = uuidv4();
    // Check for conflicts with Planroot before anything is synced to FDV, so a job that can't
    // be posted doesn't leave FDV objects behind.
    if (!(await startReading(versionGuid, sessionID))) {
        toast.error("Feil under oppstart av lesesesjon. Prøv igjen.");
        return false;
    }
    if (!(await startEditing(versionGuid, sessionID))) {
        toast.error("Versjonen kan ikke redigeres nå. Noen andre kan ha den åpen for redigering.");
        await stopReading(versionGuid, sessionID);
        return false;
    }
    if (!(await reconcileOrAbortOnConflicts(versionGuid, sessionID))) {
        await stopEditing(versionGuid, sessionID, false);
        await stopReading(versionGuid, sessionID);
        return false;
    }
    const editedFeatures = await getDifferencesObjectIds(versionGuid, sessionID);

    const objectsToPost: { objectid: string; objectGlobalId: string; layerId: number }[] = [];
    const newFlytende: Graphic[] = [];
    const editedFlytende: Graphic[] = [];

    for (const feature of featuresWithChanges) {
        const mainFeatureLayerEdits = editedFeatures.find((fe) => fe.layerId === feature.mainFeature.layerId);
        if (mainFeatureLayerEdits) {
            let allChangedObjectIds = mainFeatureLayerEdits.inserts ?? [];
            allChangedObjectIds = allChangedObjectIds.concat(mainFeatureLayerEdits.updates ?? []);
            allChangedObjectIds = allChangedObjectIds.concat(mainFeatureLayerEdits.deletes ?? []);
            if (allChangedObjectIds.includes(feature.mainFeature.objectId)) {
                objectsToPost.push({
                    objectid: feature.mainFeature.objectId,
                    objectGlobalId: feature.mainFeature.versionGraphic?.attributes.globalid || "",
                    layerId: feature.mainFeature.layerId,
                });
                if (
                    feature.mainFeature.layerId === FeatureClasses.Flytendemerke &&
                    feature.mainFeature.versionGraphic
                ) {
                    if (feature.mainFeature.versionGraphic.attributes.nfs_status === NFSStatus.PROJECTED) {
                        newFlytende.push(feature.mainFeature.versionGraphic);
                        console.log("New flytende to add:", feature.mainFeature.versionGraphic);
                    } else {
                        editedFlytende.push(feature.mainFeature.versionGraphic);
                        console.log("Edited flytende to update:", feature.mainFeature.versionGraphic);
                    }
                }
            }
        }
        for (const relatedFeature of feature.relatedFeatures) {
            const relatedFeatureLayerEdits = editedFeatures.find((fe) => fe.layerId === relatedFeature.layerId);
            if (relatedFeatureLayerEdits) {
                let allChangedObjectIds = relatedFeatureLayerEdits.inserts ?? [];
                allChangedObjectIds = allChangedObjectIds.concat(relatedFeatureLayerEdits.updates ?? []);
                allChangedObjectIds = allChangedObjectIds.concat(relatedFeatureLayerEdits.deletes ?? []);
                if (allChangedObjectIds.includes(relatedFeature.objectId)) {
                    objectsToPost.push({
                        objectid: relatedFeature.objectId,
                        objectGlobalId: relatedFeature.versionGraphic?.attributes.globalid || "",
                        layerId: relatedFeature.layerId,
                    });

                    if (relatedFeature.layerId === FeatureClasses.Flytendemerke && relatedFeature.versionGraphic) {
                        if (relatedFeature.versionGraphic.attributes.nfs_status === NFSStatus.PROJECTED) {
                            newFlytende.push(relatedFeature.versionGraphic);
                            console.log("New related flytende to add:", relatedFeature.versionGraphic);
                        } else {
                            editedFlytende.push(relatedFeature.versionGraphic);
                            console.log("Edited related flytende to update:", relatedFeature.versionGraphic);
                        }
                    }
                }
            }
        }
    }

    await stopEditing(versionGuid, sessionID, true);
    await stopReading(versionGuid, sessionID);

    const objectsByLayer: { layerId: number; objectIds: number[] }[] = [];
    for (const object of objectsToPost) {
        const layer = objectsByLayer.find((layer) => layer.layerId === object.layerId);
        if (layer) {
            // only add if not already present
            if (!layer.objectIds.includes(Number.parseInt(object.objectid))) {
                layer.objectIds.push(Number.parseInt(object.objectid));
            }
        } else {
            objectsByLayer.push({ layerId: object.layerId, objectIds: [Number.parseInt(object.objectid)] });
        }
    }

    await createWorkOrderForJob(jobObjectId.toString(), jobName, featuresWithChanges, versionName, false);

    await startReading(versionGuid, sessionID);
    await startEditing(versionGuid, sessionID);
    // Planroot may have changed while FDV objects were created. Retrying is safe since
    // features that already have a gis_obj_id are skipped by createWorkOrderForJob.
    if (!(await reconcileOrAbortOnConflicts(versionGuid, sessionID))) {
        await stopEditing(versionGuid, sessionID, false);
        await stopReading(versionGuid, sessionID);
        return false;
    }
    let result = true;

    if (objectsByLayer.length > 0) {
        console.log("POSTing", objectsByLayer);
        result = await postChanges(versionGuid, sessionID, objectsByLayer);
        // Reconcile to get changes back from post
        await reconcileVersion(versionGuid, sessionID, true, "byAttribute", false);
    }
    await stopEditing(versionGuid, sessionID, true);
    await stopReading(versionGuid, sessionID);

    if (result) {
        await updateFlytendeTildeltPosition(newFlytende, editedFlytende, true);
        const updated = await updateJobStepComment(jobStepGlobalId, comment, username);
        if (updated) {
            const newJobStepGlobalId = await createNewJobStep(jobGlobalId, JobsStatus.QA1);
            if (newJobStepGlobalId) {
                success = true;
            }
        }
    }

    return success;
};

const getJobWorkOrdersFromInvolvedObjects = async (involvedObjectGlobalIds: string[]) => {
    const workOrders: Graphic[] = [];

    const jobsWorkOrderLayer = new FeatureLayer({
        url: WorkOrderUrl(),
        id: "jobsWorkOrderLayer",
        title: "JobsWorkOrder",
    });

    const result = await jobsWorkOrderLayer.queryFeatures({
        where: `inv_nautisk_objekt_globalid IN ('${involvedObjectGlobalIds.join("','")}')`,
        outFields: ["*"],
    });

    if (result.features.length > 0) {
        workOrders.push(...result.features);
    }

    return workOrders;
};

export const updateFlytendeTildeltPosition = async (
    newFlytende: Graphic[],
    updatedFlytende: Graphic[],
    forceLocationUpdate: boolean = false,
    toBeDeleted: boolean = false
): Promise<boolean> => {
    console.log("Updating Flytende Tildelt positions...");

    if (newFlytende.length === 0 && updatedFlytende.length === 0) {
        console.log("No Flytende features to add or update.");
        return true;
    }

    const layer = getNfsLayer(FeatureClasses.FlytendemerkeTildelt);
    if (!layer) {
        console.error("Could not find Flytende Tildelt layer");
        return false;
    }

    layer.gdbVersion = "sde.DEFAULT"; // explicitly set to default to create the edits there

    const existingFlytendeTildelt =
        updatedFlytende.length > 0
            ? (
                  await layer.queryFeatures({
                      where: `fk_flytendemerke IN ('${updatedFlytende.map((f) => f.attributes.globalid).join("','")}')`,
                      outFields: toBeDeleted ? ["objectid", "globalid", "fk_flytendemerke"] : ["*"],
                      returnGeometry: toBeDeleted ? false : true,
                  })
              ).features
            : ([] as Graphic[]);

    console.log("Found existing Flytende Tildelt features to update:", existingFlytendeTildelt);

    const updatesToMake: Graphic[] = [];
    const deletesToMake: FeatureIdentifier[] = [];

    for (const updatedFlytendeFeature of updatedFlytende) {
        const existingFlytendeTildeltFeature = existingFlytendeTildelt.find(
            (ex) => ex.attributes.fk_flytendemerke === updatedFlytendeFeature.attributes.globalid
        );
        if (!existingFlytendeTildeltFeature) {
            console.warn(
                "Could not find existing Flytende Tildelt feature for updated feature:",
                updatedFlytendeFeature
            );
            continue;
        }

        if (toBeDeleted) {
            deletesToMake.push({
                objectId: existingFlytendeTildeltFeature.attributes.objectid,
                globalId: existingFlytendeTildeltFeature.attributes.globalid,
            });
        } else {
            if (
                !forceLocationUpdate &&
                updatedFlytendeFeature.geometry &&
                existingFlytendeTildeltFeature.geometry &&
                existingFlytendeTildeltFeature.attributes.malt_dato
            ) {
                const newLocationDistanceInMetersToOldLocation = distanceOperator.execute(
                    existingFlytendeTildeltFeature.geometry,
                    updatedFlytendeFeature.geometry,
                    {
                        unit: "meters",
                    }
                );
                const isWithinRadius =
                    newLocationDistanceInMetersToOldLocation !== null && newLocationDistanceInMetersToOldLocation <= 10;

                if (isWithinRadius) {
                    console.log(
                        `Skipping update for Flytende Tildelt feature ${existingFlytendeTildeltFeature.attributes.globalid} as the new location is within 10 meters of the existing location.`
                    );
                    continue;
                }
            }
            existingFlytendeTildeltFeature.attributes.er_design = forceLocationUpdate ? JaNei.Ja : JaNei.Nei;
            existingFlytendeTildeltFeature.attributes.malt_dato = forceLocationUpdate
                ? null
                : updatedFlytendeFeature.attributes.malt_dato;
            existingFlytendeTildeltFeature.attributes.malt_av = forceLocationUpdate
                ? null
                : updatedFlytendeFeature.attributes.malt_av;
            existingFlytendeTildeltFeature.geometry = updatedFlytendeFeature.geometry;
            updatesToMake.push(existingFlytendeTildeltFeature);
        }
    }

    const addsToMake: Graphic[] = newFlytende.map(
        (flytendeFeature) =>
            new Graphic({
                attributes: {
                    fk_flytendemerke: flytendeFeature.attributes.globalid,
                    sjomerke_nr: flytendeFeature.attributes.sjomerke_nr,
                    sjomerke_type: flytendeFeature.attributes.sjomerke_type,
                    er_design: JaNei.Ja,
                },
                geometry: flytendeFeature.geometry,
            })
    );

    console.log("FlytendeTildelt adds to make:", addsToMake);
    console.log("FlytendeTildelt updates to make:", updatesToMake);
    console.log("FlytendeTildelt deletes to make:", deletesToMake);

    const results = await applyEdits(layer, {
        addFeatures: addsToMake,
        updateFeatures: updatesToMake,
        deleteFeatures: deletesToMake,
    });

    if (
        results.addFeatureResults.length === newFlytende.length &&
        ((!toBeDeleted && results.updateFeatureResults.length === updatedFlytende.length) ||
            (toBeDeleted && results.deleteFeatureResults.length === updatedFlytende.length))
    ) {
        if (
            results.addFeatureResults.some((f) => f.error) ||
            results.updateFeatureResults.some((f) => f.error) ||
            results.deleteFeatureResults.some((f) => f.error)
        ) {
            console.error("Errors occurred while adding/updating/deleting Flytende Tildelt features:", results);
            return false;
        } else {
            console.log("Successfully added/updated/deleted all Flytende Tildelt features");
            return true;
        }
    } else {
        console.error("Not all Flytende Tildelt features were added/updated/deleted successfully:", results);
        console.error(results);
        return false;
    }
};

export const updateFeatureNfsStatus = async (jobFeaturesWithChanges: FeatureWithChanges[]): Promise<boolean> => {
    const allFeatureObjectsWithNfsStatus = jobFeaturesWithChanges
        .flatMap((fo) => [fo.mainFeature, ...fo.relatedFeatures])
        .filter((featureObject) => featureObject.defaultGraphic?.attributes?.nfs_status !== undefined);

    // Remove duplicates based on objectid and layerId
    // This is needed because related features can be shared between multiple main features in the job
    // or a main feature can be related to another main feature in the job
    const uniqueFeatureObjectsWithNfsStatus: FeatureObject[] = [];
    const seen = new Set<string>();
    for (const featureObject of allFeatureObjectsWithNfsStatus) {
        const identifier = `${featureObject.layerId}-${featureObject.objectId}`;
        if (!seen.has(identifier)) {
            seen.add(identifier);
            uniqueFeatureObjectsWithNfsStatus.push(featureObject);
        }
    }

    const layerGroups = uniqueFeatureObjectsWithNfsStatus.reduce(
        (groups: { [key: number]: Graphic[] }, featureObject) => {
            if (!groups[featureObject.layerId]) {
                groups[featureObject.layerId] = [];
            }
            groups[featureObject.layerId].push(featureObject.defaultGraphic!);
            return groups;
        },
        {}
    );

    const mapView = getMapView();
    if (mapView?.map === undefined || mapView?.map === null) {
        toast.error("Fant ikke kartet. Kunne ikke oppdatere status på objektene i jobben.", {
            autoClose: false,
            className: "toast-error",
        });
        return false;
    }

    const allLayers = mapView.map.allLayers.toArray().concat(mapView.map.allTables.toArray());

    let operationalObjects = 0;
    let discontinuedObjects = 0;
    let removedObjects = 0;
    let errorOccurred = false;
    const updatedFlytende: Graphic[] = [];
    const discontinuedOrRemovedFlytende: Graphic[] = [];

    try {
        // Note: Due to version locks, running applyEdits in parallel can cause issues. Rewritten to run sequentially on purpose.
        for (const layerId in layerGroups) {
            const layer = allLayers.find(
                (l) => (l as FeatureLayer).layerId === Number.parseInt(layerId)
            ) as FeatureLayer;

            // Only transition features that are currently in a *_CHANGED state. Features already in a
            // terminal state (e.g. from a prior partially-successful run) are skipped, which keeps the
            // Flytende Tildelt updates downstream idempotent — re-pushing an already-deleted flytende
            // there makes the delete-count assertion fail and breaks the whole retry.
            const updates: Graphic[] = [];
            for (const feature of layerGroups[Number.parseInt(layerId)]) {
                let newStatus = feature.attributes.nfs_status;
                let transitioned = false;
                switch (feature.attributes.nfs_status) {
                    case NFSStatus.DEPLOYED_CHANGED:
                        newStatus = NFSStatus.OPERATIVE;
                        operationalObjects++;
                        transitioned = true;
                        break;
                    case NFSStatus.REMOVED_CHANGED:
                        newStatus = NFSStatus.REMOVED;
                        removedObjects++;
                        transitioned = true;
                        break;
                    case NFSStatus.DISCONTINUED_CHANGED:
                        newStatus = NFSStatus.DISCONTINUED;
                        discontinuedObjects++;
                        transitioned = true;
                        break;
                }

                if (!transitioned) {
                    continue;
                }

                if (Number.parseInt(layerId) === FeatureClasses.Flytendemerke) {
                    if (newStatus === NFSStatus.OPERATIVE) {
                        // Need to add to Flytende Tildelt layer
                        updatedFlytende.push(feature);
                    } else if (newStatus === NFSStatus.DISCONTINUED || newStatus === NFSStatus.REMOVED) {
                        discontinuedOrRemovedFlytende.push(feature);
                    }
                }

                updates.push({
                    attributes: {
                        objectid: feature.attributes.objectid,
                        globalid: feature.attributes.globalid,
                        nfs_status: newStatus,
                    },
                } as Graphic);
            }

            if (updates.length === 0) {
                continue;
            }

            const results = await applyEdits(layer, { updateFeatures: updates }, { gdbVersion: "sde.DEFAULT" });
            if (results.updateFeatureResults.length > 0) {
                for (const result of results.updateFeatureResults) {
                    if (result.error) {
                        console.error(
                            `Error updating feature with objectid ${result.objectId} in layer ${layerId}:`,
                            result.error
                        );
                    }
                }

                if (results.updateFeatureResults.some((r) => r.error)) {
                    errorOccurred = true;
                    toast.error("Feil ved oppdatering av objekter i jobben. Status på objektene kan være feil.", {
                        autoClose: false,
                        className: "toast-error",
                    });
                }
            }

            for (const feature of updates) {
                const layerIdValue = layer?.layerId;
                const objectId = feature.attributes.objectid;
                if (
                    layerIdValue !== undefined &&
                    objectId !== undefined &&
                    layerIdValue !== null &&
                    objectId !== null
                ) {
                    dispatchRefreshFeature(layerIdValue.toString(), objectId as number, true);
                }
            }
        }

        const flytendeUpdated = await updateFlytendeTildeltPosition([], updatedFlytende);
        const flytendeDiscontinued = await updateFlytendeTildeltPosition(
            [],
            discontinuedOrRemovedFlytende,
            false,
            true
        );
        if (!flytendeUpdated || !flytendeDiscontinued) {
            errorOccurred = true;
            toast.error("Feil ved oppdatering av Flytende Tildelt-objekter i jobben.", {
                autoClose: false,
                className: "toast-error",
            });
        }
    } catch (error) {
        console.error("Error updating feature NFS status:", error);
        toast.error("En feil oppsto under oppdatering av status på objektene i jobben.", {
            autoClose: false,
            className: "toast-error",
        });
        return false;
    }

    if (errorOccurred) {
        return false;
    }

    toast.success(
        "Status oppdatert for objekter i jobben: " +
            operationalObjects +
            " operative, " +
            discontinuedObjects +
            " nedlagte, " +
            removedObjects +
            " fjernede."
    );
    return true;
};

export const getJobWorkOrdersFromJob = async (jobObjectId: string) => {
    const workOrderMapping: { layerId: number; globalId: string; workOrderId: string }[] = [];
    const involvedObjects = await getJobRelatedInvolvedObjects(jobObjectId);
    const involvedObjectGlobalIds = involvedObjects.map((obj) => obj.attributes.globalid);
    const workOrders = await getJobWorkOrdersFromInvolvedObjects(involvedObjectGlobalIds);
    for (const workOrder of workOrders) {
        const involvedObject = involvedObjects.find(
            (obj) => obj.attributes.globalid === workOrder.attributes.inv_nautisk_objekt_globalid
        );
        if (!involvedObject) continue;
        const layerId = involvedObject.attributes.nautisk_objekt_kilde;
        workOrderMapping.push({
            layerId: layerId,
            globalId: involvedObject.attributes.nautisk_objekt_globalid,
            workOrderId: workOrder.attributes.arbeidsordre_id,
        });
    }

    return workOrderMapping;
};

const getJobsByStatus = async (
    status: JobsStatus,
    creator: "ownOnly" | "othersOnly" | "all",
    creatorUsername: string
): Promise<Graphic[] | null> => {
    const jobsTableLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsTableLayer",
        title: "Jobs",
    });

    let additionalWhere = "";
    if (creator === "ownOnly") {
        additionalWhere = ` AND opprettet_av = '${creatorUsername}'`;
    } else if (creator === "othersOnly") {
        additionalWhere = ` AND opprettet_av <> '${creatorUsername}'`;
    }

    const query = jobsTableLayer.createQuery();
    query.where = `status = ${status} ${additionalWhere} AND opprettet_dato >= date '2026-01-01'`;
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.num = 1000;

    const result = await jobsTableLayer.queryFeatures(query);
    if (result.features.length > 0) {
        return result.features;
    } else {
        return null;
    }
};

export const getQA1Jobs = async (creator: "ownOnly" | "othersOnly" | "all", creatorUsername: string) => {
    return await getJobsByStatus(JobsStatus.QA1, creator, creatorUsername);
};

export const getRejectedPlanJobs = async (creator: "ownOnly" | "othersOnly" | "all", creatorUsername: string) => {
    return await getJobsByStatus(JobsStatus.PlanIkkeGodkjent, creator, creatorUsername);
};

export const getQA2Jobs = async (creator: "ownOnly" | "othersOnly" | "all", creatorUsername: string) => {
    return await getJobsByStatus(JobsStatus.QA2, creator, creatorUsername);
};

export const getAnalysisFailedJobs = async (creator: "ownOnly" | "othersOnly" | "all", creatorUsername: string) => {
    return await getJobsByStatus(JobsStatus.AnalyseFeilet, creator, creatorUsername);
};
export const getControlDeviationJobs = async (creator: "ownOnly" | "othersOnly" | "all", creatorUsername: string) => {
    return await getJobsByStatus(JobsStatus.KontrollskjermingAvvik, creator, creatorUsername);
};

export const getJobRelatedObjects = async (jobObjectId: string, relationshipName: string): Promise<Graphic[]> => {
    const jobsLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsLayer",
        title: "Jobs",
    });

    await jobsLayer.load();
    const relatedFeaturesRelationshipId = jobsLayer.relationships?.find((rel) => rel.name === relationshipName)?.id;
    if (!relatedFeaturesRelationshipId) {
        return [];
    }

    const query = new RelationshipQuery({
        relationshipId: relatedFeaturesRelationshipId,
        where: "1=1",
        objectIds: [jobObjectId],
        outFields: ["*"],
    });

    const results = await jobsLayer.queryRelatedFeatures(query);

    if (!results || results[jobObjectId] === undefined) {
        return [];
    }
    if (results[jobObjectId]?.features?.length > 0) {
        return results[jobObjectId].features;
    } else {
        return [];
    }
};

export const getJobRelatedObjectsMulti = async (
    jobObjectIds: string[],
    relationshipName: string
): Promise<Graphic[]> => {
    const jobsLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsLayer",
        title: "Jobs",
    });

    await jobsLayer.load();
    const relatedFeaturesRelationshipId = jobsLayer.relationships?.find((rel) => rel.name === relationshipName)?.id;
    if (!relatedFeaturesRelationshipId) {
        return [];
    }

    const query = new RelationshipQuery({
        relationshipId: relatedFeaturesRelationshipId,
        where: "1=1",
        objectIds: jobObjectIds,
        outFields: ["*"],
    });

    const results = await jobsLayer.queryRelatedFeatures(query);
    const stepFeatures = Object.values(results).flatMap((result: any) => result.features);
    if (stepFeatures.length > 0) {
        return stepFeatures;
    }
    return [];
};

export const getJobRelatedInvolvedObjects = async (jobObjectId: string): Promise<Graphic[]> => {
    const involvedObjects = await getJobRelatedObjects(jobObjectId, "InvolverteNautiskeObjekter");
    return involvedObjects;
};

export const getJobRelatedDocumentation = async (jobObjectId: string): Promise<Graphic[]> => {
    const documentation = await getJobRelatedObjects(jobObjectId, "Innmalingsdokumentasjon");
    return documentation;
};

export const showDocumentationPoints = async (documentation: Graphic) => {
    const mapview = getMapView();
    if (!mapview?.map) return;
    const allLayers = mapview.map.allLayers;
    let documentationLayer = allLayers.find((layer) => {
        return layer.title === "Innmålingspunkter" && layer.type === "feature";
    });
    if (documentationLayer) {
        (documentationLayer as FeatureLayer).definitionExpression =
            "dokumentasjon_globalid = '" + documentation.attributes.globalid + "'";
    } else {
        documentationLayer = new FeatureLayer({
            url: AppConfig.AdminFeatureServer + AppConfig.JobsConfig.MeasurementPointsLayerId,
            id: "measurementPointsLayer",
            title: "Innmålingspunkter",
            definitionExpression: "dokumentasjon_globalid = '" + documentation.attributes.globalid + "'",
            minScale: 0,
            popupEnabled: true,
            outFields: ["*"],
            listMode: "hide",
        });
        mapview.map.add(documentationLayer);
    }
    return documentationLayer as FeatureLayer;
};

export const hideDocumentationPoints = async () => {
    const mapview = getMapView();
    if (!mapview?.map) return;
    const allLayers = mapview.map.allLayers;
    let documentationLayer = allLayers.find((layer) => {
        return layer.title === "Innmålingspunkter" && layer.type === "feature";
    });
    if (documentationLayer) {
        (documentationLayer as FeatureLayer).definitionExpression = "1=2";
    }
};

export const updateJobStepComment = async (jobStepGlobalId: string, comment: string, completedByUsername: string) => {
    const jobsStepsLayer = new FeatureLayer({
        url: StepsUrl(),
        id: "jobsStepsLayer",
        title: "JobsSteps",
    });

    const query = jobsStepsLayer.createQuery();
    query.where = `globalid LIKE '${jobStepGlobalId}'`;
    query.outFields = ["*"];
    query.returnGeometry = false;

    const result = await jobsStepsLayer.queryFeatures(query);
    if (result.features.length > 0) {
        const jobStep = result.features[0];
        jobStep.attributes.kommentar = comment;
        jobStep.attributes.fullfort_av = completedByUsername;
        const updateResult = await applyEdits(
            jobsStepsLayer,
            {
                updateFeatures: [jobStep],
            },
            { rollbackOnFailureEnabled: true }
        );
        if (updateResult.updateFeatureResults.length > 0) {
            return true;
        } else {
            return false;
        }
    } else {
        return false;
    }
};

export const updateJobDescription = async (jobGlobalId: string, description: string): Promise<boolean> => {
    const jobsLayer = new FeatureLayer({
        url: JobsUrl(),
        id: "jobsLayer",
        title: "Jobs",
    });

    const query = jobsLayer.createQuery();
    query.where = `globalid LIKE '${jobGlobalId}'`;
    query.outFields = ["*"];
    query.returnGeometry = false;

    const result = await jobsLayer.queryFeatures(query);
    if (result.features.length > 0) {
        const job = result.features[0];
        job.attributes.beskrivelse = description;
        const updateResult = await applyEdits(
            jobsLayer,
            {
                updateFeatures: [job],
            },
            { rollbackOnFailureEnabled: true }
        );
        return updateResult.updateFeatureResults.length > 0;
    } else {
        return false;
    }
};

export const findAndGoToNauticalObject = async (relatedNauticalObject: Graphic) => {
    const layerID = relatedNauticalObject.attributes.nautisk_objekt_kilde as number;
    const nauticalObjectGlobalId = relatedNauticalObject.attributes.nautisk_objekt_globalid as string;

    const mapview = getMapView();
    if (!mapview?.map) return;

    const allLayers = mapview.map.allLayers;
    let objectLayer;
    for (const layer of allLayers) {
        if (
            layer.type === "feature" &&
            (layer as FeatureLayer).layerId === layerID &&
            (layer as FeatureLayer).url + "/" === AppConfig.NauticalObjectsFeatureServer
        ) {
            objectLayer = layer as FeatureLayer;
            break;
        }
    }

    if (!objectLayer) return;

    const query = objectLayer.createQuery();
    query.where = `globalid LIKE '${nauticalObjectGlobalId}'`;

    let feature;
    await objectLayer.queryFeatures(query).then((result) => {
        if (result.features.length > 0) {
            feature = result.features[0];
        }
    });
    if (!feature) return;

    const highlightHandle = await zoomToObject(feature, undefined, undefined, true);
    const dragHandle = mapview.on("drag", () => {
        highlightHandle?.remove();
        dragHandle.remove();
    });
};

export const formatNauticalObjects = async (
    nauticalObjects: Graphic[]
): Promise<{ objectid: number; objectGlobalId: string; layerId: number }[]> => {
    const formattedObjects: { objectid: number; objectGlobalId: string; layerId: number }[] = [];

    const mapview = getMapView();
    if (!mapview?.map) return [];

    let allLayers = mapview.map.allLayers.toArray();
    allLayers = allLayers.concat(mapview.map.allTables.toArray());

    const promises = nauticalObjects.map(async (nauticalObject) => {
        const objectGlobalId = nauticalObject.attributes.nautisk_objekt_globalid;
        const layerId = nauticalObject.attributes.nautisk_objekt_kilde;

        const layer = allLayers.find(
            (layer) =>
                layer.type === "feature" &&
                (layer as FeatureLayer).layerId === layerId &&
                (layer as FeatureLayer).url + "/" === AppConfig.NauticalObjectsFeatureServer
        ) as FeatureLayer;

        if (!layer) {
            console.warn(`Layer with ID ${layerId} not found for object ${objectGlobalId}`);
            return null;
        }

        const query = layer.createQuery();
        query.where = `globalid LIKE '${objectGlobalId}'`;
        query.outFields = ["objectid", "globalid"];

        try {
            const result = await layer.queryFeatures(query);
            if (result.features.length > 0) {
                const feature = result.features[0];
                formattedObjects.push({
                    objectid: feature.attributes.objectid,
                    objectGlobalId: objectGlobalId,
                    layerId: layerId,
                });
            }
        } catch (error) {
            console.error(`Error querying object ${objectGlobalId}:`, error);
        }

        return null;
    });

    await Promise.all(promises);
    return formattedObjects;
};

/**
 * Gets nautical objects from the NFS layer.
 * @param nauticalObjects
 * @returns
 */
export const getNFSNauticalObjects = async (nauticalObjects: Graphic[]): Promise<Graphic[]> => {
    const nfsNauticalObjects: Graphic[] = [];

    const mapview = getMapView();
    if (!mapview?.map) return nfsNauticalObjects;

    const sourceGlobalIdMapping: Record<number, string[]> = {};

    for (const nauticalObject of nauticalObjects) {
        const sourceId = nauticalObject.attributes.nautisk_objekt_kilde as number;
        const objectGlobalId = nauticalObject.attributes.nautisk_objekt_globalid as string;

        if (!sourceGlobalIdMapping[sourceId]) {
            sourceGlobalIdMapping[sourceId] = [];
        }
        sourceGlobalIdMapping[sourceId].push(objectGlobalId);
    }

    let allLayers = mapview.map.allLayers.toArray();
    allLayers = allLayers.concat(mapview.map.allTables.toArray());

    for (const sourceId in sourceGlobalIdMapping) {
        const objectGlobalIds = sourceGlobalIdMapping[sourceId];

        const layer = allLayers.find(
            (layer) =>
                layer.type === "feature" &&
                (layer as FeatureLayer).layerId === Number.parseInt(sourceId) &&
                (layer as FeatureLayer).url + "/" === AppConfig.NauticalObjectsFeatureServer
        ) as FeatureLayer;

        if (!layer) {
            console.warn(`Layer with ID ${sourceId} not found`);
            continue;
        }

        const query = layer.createQuery();
        query.where = `globalid IN ('${objectGlobalIds.join("','")}')`;
        query.outFields = ["*"];
        query.returnGeometry = true;
        query.geometryPrecision = 20;

        try {
            const result = await layer.queryFeatures(query);
            if (result.features.length > 0) {
                nfsNauticalObjects.push(...result.features);
            }
        } catch (error) {
            console.error(`Error querying NFS nautical objects:`, error);
        }
    }

    return nfsNauticalObjects;
};

export const addJobsTablesToMap = (map: arcgisCoreMap) => {
    const jobTableKeys = Object.keys(AppConfig.JobsConfig) as (keyof typeof AppConfig.JobsConfig)[];
    for (const jobTableKey of jobTableKeys) {
        if (jobTableKey === "MeasurementPointsLayerId") {
            continue;
        }
        const title = (jobTableKey as string).replace("LayerId", "");
        const existingTable = map.findTableById(title);
        if (!existingTable) {
            const layerId = AppConfig.JobsConfig[jobTableKey];
            const tableLayer = new FeatureLayer({
                url: AppConfig.AdminFeatureServer + layerId,
                layerId: layerId,
                id: title,
                title: title,
                outFields: ["*"],
                listMode: "hide",
            });
            map.tables.add(tableLayer);
        }
    }
};

export const addJobsGraphicsLayerToMap = (): GraphicsLayer | undefined => {
    const mapview = getMapView();
    if (!mapview) {
        return;
    }

    const existingLayer = mapview.map?.findLayerById("jobsGraphicsLayer");
    if (existingLayer) {
        return existingLayer as GraphicsLayer;
    }

    const jobsGraphicsLayer = new GraphicsLayer({
        id: "jobsGraphicsLayer",
        title: "Jobs Graphics Layer",
        listMode: "hide",
        visible: true,
    });

    mapview.map?.add(jobsGraphicsLayer);
    return jobsGraphicsLayer;
};

export const removeJobsGraphicsLayerFromMap = () => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const jobsGraphicsLayer = mapview.map?.findLayerById("jobsGraphicsLayer");
    if (jobsGraphicsLayer) {
        mapview.map.remove(jobsGraphicsLayer);
    }
};

export const addGraphicsToJobsGraphicsLayer = async (graphics: Graphic[]) => {
    const jobsGraphicsLayer = addJobsGraphicsLayerToMap();
    if (!jobsGraphicsLayer) return;

    for (const graphic of graphics) {
        if (!graphic.geometry) {
            continue;
        }
        const symbol = await symbolUtils.getDisplayedSymbol(graphic);
        graphic.symbol = symbol as any;
        graphic.visible = true;
        jobsGraphicsLayer.add(graphic);
    }
};

export const removeGraphicsFromJobsGraphicsLayer = (graphics: Graphic[]) => {
    const jobsGraphicsLayer = addJobsGraphicsLayerToMap();
    if (!jobsGraphicsLayer) return;
    jobsGraphicsLayer.removeMany(graphics);
};

export const clearJobsGraphicsLayer = () => {
    const jobsGraphicsLayer = addJobsGraphicsLayerToMap();
    if (!jobsGraphicsLayer) return;

    jobsGraphicsLayer.removeAll();
};

// Statuses a related object can already have that the new status must not overwrite. A function,
// not a module-level constant: nfsUtils is part of an import cycle with this file, so NFSStatus
// isn't initialized yet when this module is evaluated.
const getStatusesToKeep = (newStatus: NFSStatus): NFSStatus[] => {
    switch (newStatus) {
        case NFSStatus.PROJECTED_DISCONTINUED:
            return [
                NFSStatus.PROJECTED_DISCONTINUED,
                NFSStatus.DISCONTINUED,
                NFSStatus.PROJECTED_REMOVED,
                NFSStatus.REMOVED,
            ];
        case NFSStatus.PROJECTED_REMOVED:
            return [NFSStatus.PROJECTED_REMOVED, NFSStatus.REMOVED];
        default:
            return [newStatus];
    }
};

/**
 * Whether a related object belongs to the same combined object as its parent: child layers and
 * tables (Fundament, Lyssektor, sektorlinjer, KjettingInfo, ...) and the IB/Flytendemerke paired
 * with a Lys. Other main objects (Racon, farleder, overett, ...) and the link tables leading to
 * them keep their own status.
 */
const isPartOfCombinedObject = (parentLayerId: number, relatedLayerId: number): boolean => {
    // A direct Lys -> IB/Flytendemerke relationship is the pairing itself (see resolveSecondaryFromLys).
    // The tilknyttet_* flags on the secondary aren't reliably set on existing data, so don't rely on them.
    if (
        parentLayerId === FeatureClasses.Lys &&
        (relatedLayerId === FeatureClasses.IB || relatedLayerId === FeatureClasses.Flytendemerke)
    ) {
        return true;
    }
    if (isMainFeatureByLayerId(relatedLayerId)) return false;
    const layerConfig = AppConfig.Layers.find((l) => l.LayerId === relatedLayerId);
    return !layerConfig?.RelationshipDetails;
};

const featureKey = (feature: Graphic): string => {
    const layer = feature.layer as FeatureLayer;
    return `${layer.layerId}:${feature.attributes[layer.objectIdField]}`;
};

/**
 * The directly related objects of `parent` that belong to the same combined object and haven't
 * been visited yet. Each returned graphic has its layer bound.
 */
const getUnvisitedCombinedChildren = async (parent: Graphic, visited: Set<string>): Promise<Graphic[]> => {
    const parentLayerId = (parent.layer as FeatureLayer).layerId!;
    const children: Graphic[] = [];
    for (const { relationship, features } of await getAllRelatedFeatures(parent)) {
        const relatedLayer = getNfsLayer(relationship.relatedTableId);
        if (!relatedLayer) continue;
        for (const related of features) {
            related.layer = relatedLayer;
            const key = featureKey(related);
            if (visited.has(key)) continue;
            visited.add(key);
            if (isPartOfCombinedObject(parentLayerId, relationship.relatedTableId)) children.push(related);
        }
    }
    return children;
};

/**
 * Finds the objects that make up the same combined object as `root` and have an nfs_status.
 */
const getRelatedObjectsWithStatus = async (root: Graphic): Promise<Graphic[]> => {
    const visited = new Set<string>([featureKey(root)]);
    const combinedObjects: Graphic[] = [];
    // Depth limit mirrors findAllRelatedFeaturesForMainFeature (Lys -> Lyssektor -> Sektorlinje)
    const maxDepth = 3;
    let currentLevel: Graphic[] = [root];

    for (let depth = 0; depth < maxDepth && currentLevel.length > 0; depth++) {
        const nextLevel: Graphic[] = [];
        for (const parent of currentLevel) {
            nextLevel.push(...(await getUnvisitedCombinedChildren(parent, visited)));
        }
        combinedObjects.push(...nextLevel);
        currentLevel = nextLevel;
    }

    return combinedObjects.filter((related) => "nfs_status" in (related.attributes ?? {}));
};

/**
 * Gives the objects that make up the same combined object as `root` the same new status, so a
 * replaced, discontinued or removed object doesn't leave related objects behind as operative.
 * Returns the updated objects so they can be added to the job.
 */
const applyStatusToRelatedObjects = async (root: Graphic, newStatus: NFSStatus): Promise<FeatureObject[]> => {
    const statusesToKeep = getStatusesToKeep(newStatus);
    const relatedObjects = (await getRelatedObjectsWithStatus(root)).filter(
        (related) => !statusesToKeep.includes(related.attributes.nfs_status)
    );

    const updatesByLayer = new Map<FeatureLayer, Graphic[]>();
    for (const related of relatedObjects) {
        const layer = related.layer as FeatureLayer;
        related.attributes.nfs_status = newStatus;
        // Only send the status, so nothing else on the related object is overwritten
        const update = new Graphic({
            attributes: { [layer.objectIdField]: related.attributes[layer.objectIdField], nfs_status: newStatus },
        });
        updatesByLayer.set(layer, [...(updatesByLayer.get(layer) ?? []), update]);
    }
    for (const [layer, updateFeatures] of updatesByLayer) {
        await applyEdits(layer, { updateFeatures });
    }

    return relatedObjects.map((related) => {
        const layer = related.layer as FeatureLayer;
        return {
            type: "update",
            layerId: layer.layerId!,
            objectId: related.attributes[layer.objectIdField],
            versionGraphic: related,
        };
    });
};

export const discontinueOrRemoveNauticalObject = async (
    versionName: string,
    versionGuid: string,
    selectedFeature: Graphic,
    setSelectedFeature?: (feature: Graphic) => void,
    setShownFeature?: (feature: Graphic) => void,
    setLoading?: (loading: boolean) => void
) => {
    const statusField = AppConfig.Layers.find(
        (layerConfig) => layerConfig.Name === selectedFeature.layer?.id
    )?.StatusField;
    const newStatus = selectedFeature.attributes[statusField ?? "status"];

    if (![NFSStatus.PROJECTED_DISCONTINUED, NFSStatus.PROJECTED_REMOVED].includes(newStatus)) return;
    if ((selectedFeature.layer as FeatureLayer).gdbVersion === "sde.DEFAULT") {
        toast.error("Noe har gått galt - last siden på nytt og prøv igjen");
        return;
    }

    setLoading?.(true);
    selectedFeature.attributes[statusField ?? "status"] = newStatus;
    const actionLabel = newStatus === NFSStatus.PROJECTED_DISCONTINUED ? "Nedlegging" : "Fjerning";

    await applyEdits(selectedFeature.layer as FeatureLayer, {
        updateFeatures: [selectedFeature],
    });
    const relatedFeatures = await applyStatusToRelatedObjects(selectedFeature, newStatus);

    const sjomerkeNr = selectedFeature.attributes.sjomerke_nr;
    const gisobjid = selectedFeature.attributes.gisobjid;
    const objectid = selectedFeature.attributes.objectid;
    const navn = selectedFeature.attributes.navn;

    const objectNr = sjomerkeNr ?? gisobjid ?? objectid ?? "";
    const objectString = navn ?? "";
    const punctuation = objectNr || objectString ? ": " : "";

    const nameString = `${actionLabel} av objekt${punctuation}${objectNr} ${objectString}`;
    const versionComment = "Version:" + versionGuid;
    const featureWithChange: FeatureWithChanges[] = [];
    featureWithChange.push({
        mainFeature: {
            type: "update",
            layerId: (selectedFeature.layer as FeatureLayer).layerId!,
            objectId: selectedFeature.attributes[(selectedFeature.layer as FeatureLayer)?.objectIdField],
            versionGraphic: selectedFeature,
        },
        relatedFeatures,
    });
    const jobGlobalId = await createJobAndSetAsPlan(
        nameString,
        nameString,
        versionComment,
        featureWithChange,
        versionName,
        false,
        false
    );

    setSelectedFeature?.(selectedFeature);
    setShownFeature?.(selectedFeature);

    setLoading?.(false);
    return jobGlobalId;
};

export const replaceNauticalObject = async (
    oldObject: Graphic,
    newObject: Graphic,
    versionName: string,
    versionGuid: string
) => {
    const statusField = AppConfig.Layers.find((layerConfig) => layerConfig.Name === oldObject.layer?.id)?.StatusField;

    oldObject.attributes[statusField ?? "status"] = NFSStatus.PROJECTED_REMOVED;
    newObject.geometry = oldObject.geometry;
    if (newObject.layer) newObject.layer.visible = true;

    await applyEdits(oldObject.layer as FeatureLayer, {
        updateFeatures: [oldObject],
    });
    const oldRelatedFeatures = await applyStatusToRelatedObjects(oldObject, NFSStatus.PROJECTED_REMOVED);
    await applyEdits(newObject.layer as FeatureLayer, {
        updateFeatures: [newObject],
    });

    const featuresWithChanges: FeatureWithChanges[] = [];
    featuresWithChanges.push(
        {
            mainFeature: {
                type: "update",
                layerId: (newObject.layer as FeatureLayer)?.layerId!,
                objectId: newObject.attributes[(newObject.layer as FeatureLayer)?.objectIdField],
                versionGraphic: newObject,
            },
            relatedFeatures: [],
        },
        {
            mainFeature: {
                type: "update",
                layerId: (oldObject.layer as FeatureLayer)?.layerId!,
                objectId: oldObject.attributes[(oldObject.layer as FeatureLayer)?.objectIdField],
                versionGraphic: oldObject,
            },
            relatedFeatures: oldRelatedFeatures,
        }
    );
    const versionComment = "Version:" + versionGuid;
    return await createJobAndSetAsPlan(
        `Erstatning av objekt: ${oldObject.attributes.sjomerke_nr} ${oldObject.attributes.navn}`,
        `Erstatning av objekt: ${oldObject.attributes.sjomerke_nr} ${oldObject.attributes.navn}`,
        versionComment,
        featuresWithChanges,
        versionName,
        false,
        false
    );
};

export interface ExportToSksdResult {
    success: boolean;
    failedFeatures: { message: string }[];
    skippedFeatures: { reason: string; objectNames: string[] }[];
}

const getFeatureDisplayName = (fo: FeatureObject): string => {
    const layerName = getLayerName(fo.layerId);
    const nautClassValue = fo.defaultGraphic?.attributes["klasse"] as NauticalClasses | undefined;
    const nautClassName = nautClassValue === undefined ? undefined : NauticalClasses[nautClassValue];
    const sjomerkeNr = fo.defaultGraphic?.attributes["sjomerke_nr"] as string | undefined;
    const objectid = fo.defaultGraphic?.attributes["objectid"] as number | undefined;
    return (nautClassName ?? layerName ?? "") + " " + (sjomerkeNr ?? objectid ?? "");
};

/**
 * Exports job features with changes to SKSD.
 * Only features with valid NFS status (OPERATIVE, DISCONTINUED, REMOVED) are exported.
 * @param jobFeaturesWithChanges Array of features with changes from the job
 * @returns Result object with success status, failed features, and skipped features
 */
export const exportJobFeaturesWithChangesToSksd = async (
    jobFeaturesWithChanges: FeatureWithChanges[],
    allowCompletedNfsStatuses: boolean = false
): Promise<ExportToSksdResult> => {
    const allMainFeatures = jobFeaturesWithChanges.map((fo) => fo.mainFeature);

    // the features hold statuses before they get updated to Operative/Discontinued/Removed
    const statusesThatCanBeSent = new Set([
        NFSStatus.DEPLOYED_CHANGED,
        NFSStatus.REMOVED_CHANGED,
        NFSStatus.DISCONTINUED_CHANGED,
    ]);

    if (allowCompletedNfsStatuses) {
        statusesThatCanBeSent.add(NFSStatus.OPERATIVE);
        statusesThatCanBeSent.add(NFSStatus.DISCONTINUED);
        statusesThatCanBeSent.add(NFSStatus.REMOVED);
    }

    const layerIdsThatCanBeSent = new Set([
        FeatureClasses.Lys,
        FeatureClasses.IB,
        FeatureClasses.Racon,
        FeatureClasses.Fastsjømerke,
        FeatureClasses.Flytendemerke,
        FeatureClasses.Overettlinjelys,
        FeatureClasses.Overettlinjemerke,
    ]);

    const featuresWithValidLayer = allMainFeatures.filter((fo) => layerIdsThatCanBeSent.has(fo.layerId));

    const allMainFeaturesThatCanBeSent = featuresWithValidLayer.filter((fo) =>
        statusesThatCanBeSent.has(fo.defaultGraphic?.attributes.nfs_status)
    );

    const mainFeaturesThatCannotBeSentBecauseStatus = featuresWithValidLayer.filter(
        (fo) => !statusesThatCanBeSent.has(fo.defaultGraphic?.attributes.nfs_status)
    );

    const mainFeaturesThatCannotBeSentBecauseLayer = allMainFeatures.filter(
        (fo) => !layerIdsThatCanBeSent.has(fo.layerId)
    );

    // Removals and discontinuations are transferred by email, with the EFS message as the email body,
    // while everything else is transferred over SFTP. Depending on where in the flow the export happens,
    // the statuses may or may not already have been updated from *_CHANGED to their completed counterpart.
    const removalStatuses = new Set([
        NFSStatus.REMOVED,
        NFSStatus.REMOVED_CHANGED,
        NFSStatus.DISCONTINUED,
        NFSStatus.DISCONTINUED_CHANGED,
    ]);
    const replacementStatuses = new Set([NFSStatus.OPERATIVE, NFSStatus.DEPLOYED_CHANGED]);

    // A job can only hold more than one nautical object when a removal is replaced by a new object,
    // so any deployed/operative object in the job is the replacement for the removed one.
    const replacementFeature = allMainFeatures.find((fo) =>
        replacementStatuses.has(fo.defaultGraphic?.attributes.nfs_status)
    );
    const replacementGraphic = replacementFeature?.defaultGraphic ?? replacementFeature?.versionGraphic;

    // When a removed object is replaced by a new object of the same class (same layer and same
    // `klasse`) that is itself sent to SKSD, SKSD replaces the old object on receiving the new one,
    // so no removal message is needed for it.
    const isRemovalCoveredByReplacement = (fo: FeatureObject): boolean => {
        if (!replacementFeature || !replacementGraphic) return false;
        if (!removalStatuses.has(fo.defaultGraphic?.attributes.nfs_status)) return false;
        if (!layerIdsThatCanBeSent.has(replacementFeature.layerId)) return false;
        return (
            fo.layerId === replacementFeature.layerId &&
            fo.defaultGraphic?.attributes.klasse === replacementGraphic.attributes.klasse
        );
    };

    const removalsCoveredByReplacement = allMainFeaturesThatCanBeSent.filter(isRemovalCoveredByReplacement);
    const mainFeaturesToSend = allMainFeaturesThatCanBeSent.filter((fo) => !removalsCoveredByReplacement.includes(fo));

    const tasks = mainFeaturesToSend.map(async (fo) => {
        const isRemoval = removalStatuses.has(fo.defaultGraphic?.attributes.nfs_status);
        const emailBody = isRemoval
            ? await generateEFSMessageForRemovedOrDiscontinuedObject(fo.defaultGraphic!, replacementGraphic)
            : "";
        return exportFeatureToSksd(fo.layerId, fo.defaultGraphic!.getGlobalId()!, emailBody);
    });

    const settledResults = await Promise.allSettled(tasks);

    const failed: { message: string }[] = [];
    settledResults.forEach((result) => {
        if (result.status === "fulfilled") {
            if (!result.value.success) {
                const defaultGlobalId = `{${result.value.globalId.toUpperCase()}}`;
                const feature = mainFeaturesToSend.find((fo) => fo.defaultGraphic?.getGlobalId() === defaultGlobalId);
                const featureName = feature ? getFeatureDisplayName(feature) : "Ukjent objekt";
                failed.push({ message: `${featureName}: ${result.value.message || "Ukjent feil"}` });
            }
        } else {
            failed.push({
                message: result.reason instanceof Error ? result.reason.message : String(result.reason),
            });
        }
    });

    const skippedFeatures: { reason: string; objectNames: string[] }[] = [];

    const notSentBecauseReplaced = removalsCoveredByReplacement.map(getFeatureDisplayName);
    if (notSentBecauseReplaced.length > 0) {
        skippedFeatures.push({
            reason: "Fjerning erstattes av nytt objekt i samme klasse, SKSD varsles kun om det nye objektet",
            objectNames: notSentBecauseReplaced,
        });
    }

    const notSentBecauseStatus = mainFeaturesThatCannotBeSentBecauseStatus.map(getFeatureDisplayName);
    if (notSentBecauseStatus.length > 0) {
        skippedFeatures.push({
            reason: "Ugyldig NFS-status",
            objectNames: notSentBecauseStatus,
        });
    }

    const notSentBecauseLayer = mainFeaturesThatCannotBeSentBecauseLayer.map(getFeatureDisplayName);
    if (notSentBecauseLayer.length > 0) {
        skippedFeatures.push({
            reason: "Objekter som ikke sendes til SKSD",
            objectNames: notSentBecauseLayer,
        });
    }

    return {
        success: failed.length === 0,
        failedFeatures: failed,
        skippedFeatures,
    };
};

export const objectShouldValidate = (featureWithChanges: FeatureWithChanges): boolean => {
    return (
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.PROJECTED_REMOVED &&
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.PROJECTED_DISCONTINUED &&
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.REMOVED_CHANGED &&
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.DISCONTINUED_CHANGED &&
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.REMOVED &&
        featureWithChanges.mainFeature.versionGraphic?.attributes.nfs_status !== NFSStatus.DISCONTINUED
    );
};

export const objectsShouldValidate = (jobType: JobsType): boolean =>
    ![JobsType.REMOVAL, JobsType.DISCONTINUATION].includes(jobType);

/** True while any object that should validate has not yet received a validation result. */
export const jobValidationInProgress = (features: FeatureWithChanges[]): boolean =>
    features.some((f) => objectShouldValidate(f) && !f.validationResult);

/**
 * True when every object that should validate has a valid result. `validateObjects` is the
 * job-type gate (objectsShouldValidate); when false validation is not required, so this is
 * true regardless of the features.
 */
export const allJobObjectsAreValid = (features: FeatureWithChanges[], validateObjects: boolean): boolean => {
    if (!validateObjects) return true;
    return features.every((f) => !objectShouldValidate(f) || !!f.validationResult?.isValid);
};

export const applyValidationResults = async (
    objects: FeatureWithChanges[],
    setJobFeaturesWithChanges: (features: FeatureWithChanges[]) => void,
    versionName: string | null,
    validationContext: "PostToDefault" | "SendToSKSD"
): Promise<void> => {
    const featuresWithChanges = await Promise.all(
        objects.map(async (featureWithChange) => {
            if (!objectShouldValidate(featureWithChange)) {
                return featureWithChange;
            }
            const mainFeature = featureWithChange.mainFeature;
            try {
                const validationResult = await getValidationResult(
                    mainFeature.versionGraphic!.attributes.globalid,
                    mainFeature.layerId,
                    versionName,
                    validationContext
                );

                featureWithChange.validationResult = validationResult;
                return featureWithChange;
            } catch (error) {
                console.error(
                    "Error fetching validation result for ",
                    mainFeature.layerId,
                    mainFeature.versionGraphic!.attributes.globalid,
                    error
                );
                toast.error(
                    `Feil ved henting av valideringsresultat for ${mainFeature.versionGraphic!.attributes.sjomerke_nr}.`
                );
                featureWithChange.validationResult = {
                    globalId: mainFeature.versionGraphic!.attributes.globalid,
                    layerId: mainFeature.layerId,
                    isValid: false,
                    errors: [
                        {
                            attributeName: "Feil ved henting av valideringsresultat",
                        } as AttributeValidationError,
                    ],
                } as NauticalObjectValidationResult;
                return featureWithChange;
            }
        })
    );
    setJobFeaturesWithChanges(featuresWithChanges);
};

/**
 * Sets the "siste_kontrollskjerming_dato" field to the current date for a given feature. This is used to track when a feature was last included in a control deviation job.
 * @param feature
 */
export const setFieldSistKontrolSkjermingDate = async (feature: Graphic) => {
    const layer = feature?.layer as FeatureLayer;
    if (!layer) {
        console.error("Feature does not have a layer assigned:", feature);
        return false;
    }
    const fieldName = "siste_kontrollskjerming_dato";
    const editToApply = new Graphic({
        attributes: {
            objectid: feature.attributes.objectid,
            globalid: feature.attributes.globalid,
            [fieldName]: Date.now(),
        },
    });
    try {
        await applyEdits(
            layer,
            {
                updateFeatures: [editToApply],
            },
            { gdbVersion: "sde.DEFAULT" }
        );
        return true;
    } catch (error) {
        console.error("Error updating feature:", error);
        return false;
    }
};
